import { zodResolver } from '@hookform/resolvers/zod'
import { ReactNode, useCallback, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { PasswordInputFormField } from '@/components/form'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { TabActions } from '@/components/layout/tab-card'
import { toastFactory } from '@/factories'
import { getApiErrorMessage } from '@/utils/error'

import { FormChangeFields, getFormChanges } from '../../../../shared/form-changes'
import { ProviderCard } from './provider-card'
import { useBrivoCredentials, useSaveBrivoCredentials } from './provider-connections'
import { ProviderField } from './provider-field'
import { SecretValue } from './secret-value'
import { maskSecret, ProviderDirtyChange, useProviderEdit } from './use-provider-edit'

const BRIVO_FIELDS = [
  'clientId',
  'clientSecret',
  'sdkClientId',
  'sdkClientSecret',
  'apiKey',
  'username',
  'password',
] as const

type BrivoField = (typeof BRIVO_FIELDS)[number]
type BrivoFormData = Record<BrivoField, string>

const SECRET_FIELDS: readonly BrivoField[] = [
  'clientSecret',
  'sdkClientSecret',
  'apiKey',
  'password',
]

// Note: the endpoint only returns these two, so a connected Brivo shows just them
const SAVED_FIELDS: readonly BrivoField[] = ['clientId', 'clientSecret']

interface BrivoCardProps {
  buildingId: number
  onDirtyChange: ProviderDirtyChange
  tabs: ReactNode
}

export const BrivoCard = ({ buildingId, onDirtyChange, tabs }: BrivoCardProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  const { clientId, clientSecret, configured, isLoading } = useBrivoCredentials(buildingId)
  const { mutateAsync: save, isPending } = useSaveBrivoCredentials()

  const schema = useMemo(
    () =>
      z.object(
        Object.fromEntries(
          BRIVO_FIELDS.map((field) => [
            field,
            z
              .string()
              .trim()
              .min(1, t(`organisation:places.integrations.brivo.errors.${field}`)),
          ]),
        ) as Record<BrivoField, z.ZodString>,
      ),
    [t],
  )

  const serverValues = useMemo(
    () =>
      Object.fromEntries(
        BRIVO_FIELDS.map((field) => [
          field,
          field === 'clientId' ? clientId : field === 'clientSecret' ? clientSecret : '',
        ]),
      ) as BrivoFormData,
    [clientId, clientSecret],
  )

  const form = useForm<BrivoFormData>({
    defaultValues: serverValues,
    mode: 'onTouched',
    resolver: zodResolver(schema),
    values: serverValues,
  })

  const changeFields = useMemo<FormChangeFields<BrivoFormData>>(
    () =>
      Object.fromEntries(
        BRIVO_FIELDS.map((field) => [
          field,
          {
            label: t(`organisation:places.integrations.brivo.fields.${field}.label`),
            ...(SECRET_FIELDS.includes(field) && { format: maskSecret }),
          },
        ]),
      ),
    [t],
  )

  const getChanges = useCallback(
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    [changeFields, form],
  )

  const { cancel, isEditing, start, stop } = useProviderEdit({
    form,
    getChanges,
    onDirtyChange,
    serverValues,
  })

  const handleSave = form.handleSubmit(async (values) => {
    try {
      await save({ buildingId, ...values })
      toastFactory.success(t('organisation:places.integrations.keyless.toasts.saved'))
      stop()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  })

  const fields = configured ? SAVED_FIELDS : BRIVO_FIELDS

  return (
    <FormProvider {...form}>
      <ProviderCard
        actions={
          configured ? undefined : (
            <TabActions
              isDirty={form.formState.isDirty}
              isEditing={isEditing}
              isSaving={isPending}
              onCancel={cancel}
              onEdit={start}
              onSave={handleSave}
            />
          )
        }
        configured={configured}
        loading={isLoading}
        skeletonGroups={[
          {
            labels: BRIVO_FIELDS.map((field) =>
              t(`organisation:places.integrations.brivo.fields.${field}.label`),
            ),
          },
        ]}
        tabs={tabs}
      >
        {fields.map((field) => {
          const placeholder = t(
            `organisation:places.integrations.brivo.fields.${field}.placeholder`,
          )
          const isSecret = SECRET_FIELDS.includes(field)

          return (
            <ProviderField
              key={field}
              required
              isEditing={isEditing}
              label={t(`organisation:places.integrations.brivo.fields.${field}.label`)}
              locked={configured}
              view={
                isSecret && serverValues[field] ? (
                  <SecretValue value={serverValues[field]} />
                ) : (
                  serverValues[field]
                )
              }
            >
              {isSecret ? (
                <PasswordInputFormField
                  autoComplete="new-password"
                  name={field}
                  placeholder={placeholder}
                  size="small"
                />
              ) : (
                <TextInputFormField
                  clearable
                  withRestore
                  autoComplete="off"
                  name={field}
                  placeholder={placeholder}
                  size="small"
                />
              )}
            </ProviderField>
          )
        })}
      </ProviderCard>
    </FormProvider>
  )
}
